/** @jsxImportSource @emotion/react */
import 'twin.macro';

import { ReactComponent as UsageTrackingSvg } from 'assets/Icons/usage-tracking.svg';
import { QuestionHint } from 'components/shared/QuestionHint';
import { mediumTooltipCss } from 'components/shared/twin.styles';
import { SummaryMetricsSection } from './SummaryMetricsSection';
import { DetailedUsageTable } from './DetailedUsageTable';
import { useCallback, useState, useMemo } from 'react';
import { useEffectOnce, useToggle } from 'react-use';
import { UsageSummaryMetrics, UsageTrackingService } from 'services/usage-tracking.service';
import { Info } from 'components/shared/Info';
import { useSelector } from 'react-redux';
import { RootState } from 'reducers';
import { tierSelector } from 'slices/payment.slice';
import { isEmpty } from 'lodash';

type Props = {};

// eslint-disable-next-line no-empty-pattern
export const UsageTrackingSection = (props: Props) => {
  const apiKeys = useSelector((state: RootState) => state.apiKey.items);
  const tierInfo = useSelector(tierSelector);
  
  const hasApiKeys = !isEmpty(apiKeys);
  
  // All non-Trial/PayG plans are eligible for usage tracking
  const isUsageTrackingEligible = useMemo(() => 
     !(tierInfo.isTrial || tierInfo.isPAYG),
    [tierInfo.isTrial, tierInfo.isPAYG]
  );

  const shouldShowUsageTracking = isUsageTrackingEligible && hasApiKeys;

  const [summaryMetrics, setSummaryMetrics] =
    useState<UsageSummaryMetrics | null>(null);
  const [isSummaryLoading, toggleSummaryLoading] = useToggle(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    try {
      setSummaryError(null);
      toggleSummaryLoading(true);
      const response = await UsageTrackingService.getUsageSummary();
      setSummaryMetrics(response.data.data);
    } catch (error) {
      console.log('Error fetching usage summary >>>', error);
      setSummaryMetrics(null);
      setSummaryError('Unable to load usage summary. Please try again.');
    } finally {
      toggleSummaryLoading(false);
    }
  }, [toggleSummaryLoading]);

  useEffectOnce(() => {
    if (shouldShowUsageTracking) {
      fetchSummary();
    }
  });

  // Don't render anything if not eligible and no API keys
  if (!shouldShowUsageTracking) {
    return (
      <div tw="flex flex-col mt-6">
        {isUsageTrackingEligible && !hasApiKeys && (
          <div tw="ml-4 md-down:ml-0">
            <Info text="Create an API key to unlock usage tracking." />
          </div>
        )}
      </div>
    );
  }

  return (
    <div tw="flex flex-col mt-6">
      <div tw="flex items-center space-x-4 ml-4 md-down:(ml-0 space-x-2) flex-wrap">
        <UsageTrackingSvg width={24} height={24} tw="md-down:(width[20px] height[20px])" />

        <div tw="font-bold text-4xl md-down:text-3xl sm-down:text-2xl">Usage tracking</div>
        <QuestionHint
          notTransparent
          hideCloseButton
          type={'information'}
          align={'bottomLeft'}
          action={'hover'}
          text={
            <div tw="py-1">
              Sonnant will track usage of the API keys for auditing and billing
              purposes.
            </div>
          }
          customStyle={mediumTooltipCss}
        />
      </div>

      <SummaryMetricsSection
        metrics={summaryMetrics}
        isLoading={isSummaryLoading}
      />

      {summaryError && (
        <div tw="ml-4 md-down:ml-0">
          <Info text={summaryError} />
        </div>
      )}

      <DetailedUsageTable />
    </div>
  );
};
